/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Button, CheckIcon, StoreBadges } from '../common';
import { colors, borderRadius, typography } from '../../styles/theme';

const sectionStyles = css`
  background: linear-gradient(135deg, ${colors.primary[700]} 0%, ${colors.primary[900]} 100%);
  color: white;
  max-width: 100%;
  padding: 4rem 1rem;

  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

const containerStyles = css`
  max-width: 1200px;
  margin: 0 auto;
`;

const contentStyles = css`
  display: grid;
  gap: 3rem;
  align-items: center;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const textStyles = css`
  h2 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize['2xl']};
    font-weight: ${typography.fontWeight.extrabold};
    color: white;
    margin-bottom: 1rem;

    @media (min-width: 768px) {
      font-size: ${typography.fontSize['3xl']};
    }
  }

  p {
    color: ${colors.primary[100]};
    margin-bottom: 2rem;
    line-height: 1.7;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0 0 2rem 0;

    li {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 0.75rem;
      font-size: ${typography.fontSize.base};

      .check {
        width: 24px;
        height: 24px;
        background: ${colors.success[500]};
        border-radius: ${borderRadius.full};
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        svg {
          width: 13px;
          height: 13px;
        }
      }
    }
  }
`;

const storeBadgesStyles = css`
  margin-bottom: 1.5rem;
`;

const exploreButtonStyles = css`
  @media (max-width: 479px) {
    width: 100%;
  }
`;

const imageStyles = css`
  display: flex;
  justify-content: center;

  .phone-mockup {
    width: 240px;
    aspect-ratio: 9 / 19;
    background: linear-gradient(180deg, ${colors.neutral[800]} 0%, ${colors.neutral[900]} 100%);
    border-radius: 36px;
    padding: 10px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);

    @media (min-width: 768px) {
      width: 280px;
      border-radius: 40px;
      padding: 12px;
    }

    .screen {
      width: 100%;
      height: 100%;
      background: white;
      border-radius: 28px;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top;
      }
    }
  }
`;

interface AppPromoSectionProps {
  onOpenModal: () => void;
}

export default function AppPromoSection({ onOpenModal }: AppPromoSectionProps) {
  return (
    <section css={sectionStyles}>
      <div css={containerStyles}>
        <div css={contentStyles}>
          <div css={textStyles}>
            <h2>YolSepetiGO Mobil Uygulaması</h2>
            <p>
              Tek tıkla çekici, vinç, yol yardım veya nakliye hizmeti talep edin.
              Konum paylaşımı, anlık takip ve güvenli ödeme seçenekleriyle
              en hızlı yardımı alın.
            </p>
            <ul>
              <li><span className="check"><CheckIcon /></span> Anlık konum paylaşımı</li>
              <li><span className="check"><CheckIcon /></span> Canlı sürücü takibi</li>
              <li><span className="check"><CheckIcon /></span> Güvenli ödeme seçenekleri</li>
              <li><span className="check"><CheckIcon /></span> 7/24 müşteri desteği</li>
            </ul>
            <StoreBadges
              appStoreUrl="https://apps.apple.com/us/app/yol-sepetigo/id6760605675"
              playStoreUrl="https://play.google.com"
              css={storeBadgesStyles}
            />
            <Button variant="secondary" size="lg" onClick={onOpenModal} css={exploreButtonStyles}>
              Mobil Uygulamayı İncele
            </Button>
          </div>
          <div css={imageStyles}>
            <div className="phone-mockup">
              <div className="screen">
                <img
                  src="/images/promo/1.png"
                  alt="YolSepetiGO uygulaması ana ekranı"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
